import { useEffect } from 'react';

import { RESTRICTIONS_TYPE } from '@/_config';
import { useMeInstructorDependencyQuery } from '@/_instructor/profile/ts-actions';
import { getAccountType, isInstructorAccount, isLoggedIn, logger, setCookie } from '@/_shared';

import type { TCustomerMeModel, TInstructorMeModel } from './model';

import { useMeDependencyQuery } from '../profile/persistent-actions';

export function customerTransformResponse(data: TCustomerMeModel) {
  const accountType = getAccountType();
  const isInstructorUser = isInstructorAccount();

  const { id } = data;
  setCookie('atp_uid', id);

  return {
    ...data,
    localAccountType: accountType,
    isInstructorUser,
  } as TCustomerMeModel;
}

export function instructorTransformResponse(data: TInstructorMeModel) {
  const accountType = getAccountType();
  const isInstructorUser = isInstructorAccount();

  setCookie('atp_uid', data?.id);
  if (data?.id) {
    logger.setId({ customer_id: data.id });
  }

  return {
    ...data,
    accessType: RESTRICTIONS_TYPE.ALL_ACCESS,
    localAccountType: accountType,
    isInstructorUser,
  } as TInstructorMeModel;
}

export function useMeDependency(reloadData = false) {
  const isInstructorUser = isInstructorAccount();
  const isLoggedInUser = isLoggedIn();

  const {
    data: customerData,
    isSuccess: customerSuccess,
    isError: customerError,
    isFetching: customerIsFetching,
    isLoading: customerIsLoading,
    refetch: customerRefetch,
  } = useMeDependencyQuery({ enabled: isLoggedInUser && !isInstructorUser });

  const {
    data: instructorData,
    isSuccess: instructorSuccess,
    isError: instructorError,
    isFetching: instructorIsFetching,
    isLoading: instructorIsLoading,
    refetch: instructorRefetch,
  } = useMeInstructorDependencyQuery({ enabled: isLoggedInUser && isInstructorUser });

  const success = isInstructorUser ? instructorSuccess : customerSuccess;
  const error = isInstructorUser ? instructorError : customerError;
  const data = isInstructorUser ? instructorData : customerData;
  const preload = isInstructorUser ? instructorIsFetching : customerIsFetching;
  const isLoading = isInstructorUser ? instructorIsLoading : customerIsLoading;
  const refetch = isInstructorUser ? instructorRefetch : customerRefetch;

  useEffect(() => {
    if (reloadData) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadData]);

  return {
    data: (data || {}) as TCustomerMeModel | TInstructorMeModel,
    success,
    error,
    preload,
    isLoading,
    refetch,
  };
}
